import { TourPlacement } from "./types";

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const CARD_WIDTH = 340;
const CARD_GAP = 14;
const VIEWPORT_MARGIN = 12;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Where the card goes beside its target. It takes the side asked for when the
 * card fits there, the opposite side when it does not, and is always kept
 * inside the viewport: a card the user cannot see cannot be closed either.
 */
export const cardPositionOf = ({
  target,
  placement,
  cardHeight,
  viewport,
}: {
  target: Rect | null;
  placement: TourPlacement;
  cardHeight: number;
  viewport: { width: number; height: number };
}): { top: number; left: number } => {
  if (!target) {
    return {
      top: Math.max(VIEWPORT_MARGIN, (viewport.height - cardHeight) / 2),
      left: Math.max(VIEWPORT_MARGIN, (viewport.width - CARD_WIDTH) / 2),
    };
  }

  const room = {
    top: target.top,
    bottom: viewport.height - (target.top + target.height),
    left: target.left,
    right: viewport.width - (target.left + target.width),
  };
  const needed = {
    top: cardHeight + CARD_GAP,
    bottom: cardHeight + CARD_GAP,
    left: CARD_WIDTH + CARD_GAP,
    right: CARD_WIDTH + CARD_GAP,
  };
  const opposite: Record<TourPlacement, TourPlacement> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
  };
  const fits = (candidate: TourPlacement) => room[candidate] >= needed[candidate];

  // A target that fills the screen leaves no side to stand on: the card goes
  // inside it, in the bottom right corner, where it covers the least.
  if (!fits(placement) && !fits(opposite[placement])) {
    return {
      top: clamp(
        target.top + target.height - cardHeight - CARD_GAP,
        VIEWPORT_MARGIN,
        viewport.height - cardHeight - VIEWPORT_MARGIN,
      ),
      left: clamp(
        target.left + target.width - CARD_WIDTH - CARD_GAP,
        VIEWPORT_MARGIN,
        viewport.width - CARD_WIDTH - VIEWPORT_MARGIN,
      ),
    };
  }

  const side = fits(placement) ? placement : opposite[placement];

  const centeredLeft = target.left + target.width / 2 - CARD_WIDTH / 2;
  const centeredTop = target.top + target.height / 2 - cardHeight / 2;
  const raw = {
    top: { top: target.top - cardHeight - CARD_GAP, left: centeredLeft },
    bottom: { top: target.top + target.height + CARD_GAP, left: centeredLeft },
    left: { top: centeredTop, left: target.left - CARD_WIDTH - CARD_GAP },
    right: { top: centeredTop, left: target.left + target.width + CARD_GAP },
  }[side];

  return {
    top: clamp(raw.top, VIEWPORT_MARGIN, viewport.height - cardHeight - VIEWPORT_MARGIN),
    left: clamp(raw.left, VIEWPORT_MARGIN, viewport.width - CARD_WIDTH - VIEWPORT_MARGIN),
  };
};

export const sameRect = (left: Rect | null, right: Rect | null) =>
  left === right ||
  (left !== null &&
    right !== null &&
    left.top === right.top &&
    left.left === right.left &&
    left.width === right.width &&
    left.height === right.height);
